import { useEffect, useMemo, useState } from 'react';
import {
  STAGE1_ARTIFACT_STORAGE_EVENT,
  countAnalysisFlags,
  formatStrategyName,
  loadStoredStage1ArtifactBundle,
  shortChecksum,
  stage1DemoSummary,
  type Stage1ArtifactBundle,
  type Stage1Summary,
} from '@/features/research/stage1';
import type { WidgetDefinition, WidgetProps } from '@/types/widget';

function statusColor(ok: boolean): string {
  return ok ? 'var(--sp-ok)' : 'var(--sp-error)';
}

function loadWidgetBundle(): Stage1ArtifactBundle | null {
  return loadStoredStage1ArtifactBundle();
}

function SummaryBadge({ label, ok }: { label: string; ok: boolean }) {
  return (
    <span
      className="mono"
      style={{
        border: `1px solid ${statusColor(ok)}`,
        borderRadius: 'var(--sp-radius)',
        color: statusColor(ok),
        padding: '2px 6px',
        fontSize: 10,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

function StrategyPreview({ summary }: { summary: Stage1Summary }) {
  return (
    <div style={{ display: 'grid', gap: 5 }}>
      {summary.strategy_scorecard.strategies.slice(0, 3).map((strategy) => (
        <div
          key={strategy.strategy}
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto',
            gap: 8,
            alignItems: 'center',
            fontSize: 12,
          }}
        >
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {formatStrategyName(strategy.strategy)}
          </span>
          <span className="mono" style={{ color: 'var(--sp-accent)', fontSize: 11 }}>
            {strategy.goals_met}/{strategy.goals_assessed}
          </span>
        </div>
      ))}
    </div>
  );
}

function ResearchStage1SummaryWidget(_: WidgetProps) {
  const [bundle, setBundle] = useState<Stage1ArtifactBundle | null>(() => loadWidgetBundle());

  useEffect(() => {
    const refresh = () => setBundle(loadWidgetBundle());
    window.addEventListener(STAGE1_ARTIFACT_STORAGE_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(STAGE1_ARTIFACT_STORAGE_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  const summary = bundle?.summary ?? stage1DemoSummary;
  const isDemo = !bundle;
  const flagCount = useMemo(() => countAnalysisFlags(summary), [summary]);
  const caveatCount = summary.caveats.length;
  const managedExecution = (summary as Stage1Summary & { managed_execution?: { synthetic?: boolean } }).managed_execution;
  const sourceText = isDemo
    ? 'Demo artifact shown until a Stage 1 summary is loaded.'
    : bundle.source === 'managed'
      ? 'Using the latest managed Stage 1 worker result.'
      : 'Using the latest locally loaded Stage 1 summary.';

  return (
    <div className="sp-widget">
      <div className="sp-widget-header">
        <span>Research Stage 1</span>
        <a href="/research" style={{ fontSize: 11, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
          Open workbench
        </a>
      </div>
      <div className="sp-widget-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {summary.run_id}
            </div>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 10, overflowWrap: 'anywhere' }}>
              {shortChecksum(summary.result_fingerprint)}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'flex-end' }}>
            <SummaryBadge ok={summary.benchmark.passed} label={summary.benchmark.passed ? 'green' : 'red'} />
            {bundle?.source === 'managed' ? (
              <SummaryBadge
                ok={managedExecution?.synthetic === false}
                label={managedExecution?.synthetic === false ? 'real data' : 'synthetic'}
              />
            ) : null}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6 }}>
          <div style={{ borderTop: '1px solid var(--sp-border)', paddingTop: 6 }}>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 9, textTransform: 'uppercase' }}>
              Benchmark
            </div>
            <div style={{ fontWeight: 700 }}>{summary.benchmark.passed_count}/{summary.benchmark.total}</div>
          </div>
          <div style={{ borderTop: '1px solid var(--sp-border)', paddingTop: 6 }}>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 9, textTransform: 'uppercase' }}>
              Caveats
            </div>
            <div style={{ fontWeight: 700, color: caveatCount > 0 ? 'var(--sp-warn)' : 'var(--sp-ok)' }}>{caveatCount}</div>
          </div>
          <div style={{ borderTop: '1px solid var(--sp-border)', paddingTop: 6 }}>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 9, textTransform: 'uppercase' }}>
              Flags
            </div>
            <div style={{ fontWeight: 700, color: flagCount > 0 ? 'var(--sp-warn)' : 'var(--sp-ok)' }}>{flagCount}</div>
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--sp-border)', paddingTop: 8 }}>
          <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 10, textTransform: 'uppercase', marginBottom: 5 }}>
            Scorecard preview
          </div>
          <StrategyPreview summary={summary} />
        </div>

        <div style={{ marginTop: 'auto', color: 'var(--sp-text-muted)', fontSize: 11 }}>
          {sourceText}
        </div>
      </div>
    </div>
  );
}

export const researchStage1SummaryWidget: WidgetDefinition = {
  id: 'research-stage1-summary',
  name: 'Research Stage 1',
  description: 'Stage 1 bake-off evidence summary with click-through to /research.',
  icon: 'flask-conical',
  category: 'workspace',
  scope: 'both',
  defaultSize: { w: 5, h: 4 },
  minSize: { w: 3, h: 3 },
  component: ResearchStage1SummaryWidget,
};
